import { MENU_REGISTRY } from './menu-registry';

/**
 * PURE resolver for the Permission Access Layer (SA-1592 Phase A0).
 *
 * Mirrors the longest-prefix resolution of `resolveActiveMenuAccess` in
 * `authUtils.js`, but operates on a passed-in `menus` array instead of reading
 * localStorage. No React, no localStorage — fully testable as a pure function.
 *
 * @param {Array} menus - decrypted menus array (each menu may carry `sub_menus`).
 * @param {string} keyOrPathname - a MENU key (from menu-registry) or a raw pathname.
 * @returns {object|null} the matching menu object, or null when not found (fail-closed).
 */

// Pathname-level aliases derived from MENU_REGISTRY entries that carry
// `aliasOf` — mirrors ROUTE_ACCESS_ALIASES in authUtils.js, but kept in a
// single source (the registry) so it cannot drift. Each alias maps a
// sub-flow endpoint prefix to the owning menu's endpoint.
const PATHNAME_ALIASES = Object.keys(MENU_REGISTRY).reduce((acc, key) => {
  const entry = MENU_REGISTRY[key];
  const target = entry?.aliasOf ? MENU_REGISTRY[entry.aliasOf] : null;
  if (entry?.endpoint && target?.endpoint) {
    acc.push({ prefix: entry.endpoint, resolveAs: target.endpoint });
  }
  return acc;
}, []);

const applyPathnameAlias = (pathname) => {
  if (typeof pathname !== 'string') return null;

  const alias = PATHNAME_ALIASES.find(
    (entry) =>
      pathname === entry.prefix || pathname.startsWith(`${entry.prefix}/`)
  );
  return alias ? alias.resolveAs : pathname;
};

// Resolve a MENU key (honoring `aliasOf`) to its concrete endpoint. A raw
// pathname that is not a registry key is run through the pathname alias map
// so sub-flow routes resolve against their owning menu's endpoint.
const resolveEndpoint = (keyOrPathname) => {
  const entry = MENU_REGISTRY[keyOrPathname];
  if (!entry) return applyPathnameAlias(keyOrPathname);

  if (entry.aliasOf) {
    const target = MENU_REGISTRY[entry.aliasOf];
    return target?.endpoint ?? entry.endpoint ?? null;
  }
  return entry.endpoint ?? null;
};

// Flatten menus into the list of candidate access objects: a menu's sub_menus
// when present, otherwise the menu itself. Mirrors resolveActiveMenuAccess.
const collectCandidates = (menus) => {
  const candidates = [];
  menus.forEach((menu) => {
    const subMenus = menu?.sub_menus;
    if (Array.isArray(subMenus) && subMenus.length > 0) {
      subMenus.forEach((sub) => candidates.push(sub));
      return;
    }
    candidates.push(menu);
  });
  return candidates;
};

export const resolveAccess = (menus, keyOrPathname) => {
  if (!Array.isArray(menus)) return null;

  const candidates = collectCandidates(menus);

  // Slug match takes priority over endpoint match. It resolves menus whose
  // `frontend_endpoint` is empty (e.g. Quality Checker, id 59) and therefore
  // cannot be matched by path. Only registry entries that declare a `slug`
  // opt into this branch — every other menu falls through to endpoint match.
  const registryEntry = MENU_REGISTRY[keyOrPathname];
  if (registryEntry?.slug) {
    const bySlug = candidates.find(
      (candidate) => candidate?.slug === registryEntry.slug
    );
    if (bySlug) return bySlug;
  }

  const endpoint = resolveEndpoint(keyOrPathname);
  if (!endpoint) return null;

  let best = null;
  let bestLength = -1;
  candidates.forEach((candidate) => {
    const candidateEndpoint = candidate?.frontend_endpoint;
    if (!candidateEndpoint) return;

    const isPrefix =
      endpoint === candidateEndpoint ||
      endpoint.startsWith(`${candidateEndpoint}/`);
    if (isPrefix && candidateEndpoint.length > bestLength) {
      best = candidate;
      bestLength = candidateEndpoint.length;
    }
  });

  return best;
};
